/** Shared helpers for the Deals dashboard tab. */

/** Bump when cached schema field list / toggles change (invalidates in-memory cache). */
export const DEAL_FORMS_SCHEMA_CACHE_VERSION = 4

export const DRAFT_STORAGE_KEY = 'encore.deals.draft'
const SCHEMA_CACHE_VERSION_KEY = 'encore.deals.schemaCacheVersion'

const MARKETING_NO_CAP_TOKENS = new Set([
  'ma',
  'm.a.',
  'mutually approved',
  'mutually approved marketing budget',
])

const LEGAL_OMIT_TOKENS = new Set(['', 'na', 'n/a', 'n.a.', 'none', 'omit', 'skip', '-'])

export const FIELD_GROUPS = [
  {
    id: 'artist',
    title: 'Artist & Parties',
    fields: new Set([
      'ArtistName',
      'ArtistLegalName',
      'ArtistAddressAtty',
      'AttorneyName',
      'ArtistEmail',
      'SalutationName',
      'EffectiveDate',
    ]),
  },
  {
    id: 'term',
    title: 'Term & Recordings',
    fields: new Set([
      'NumTracks',
      'TermLength',
      'BufferLength',
      'LicenseLength',
      'MatchLength',
      'NumOptions',
      'NumOptionTracks',
    ]),
  },
  {
    id: 'money',
    title: 'Money',
    fields: new Set([
      'AdvanceAmount',
      'ExecutionAmount',
      'RedeliveryAmount',
      'MarketingAmount',
      'DiscretionaryMktg',
      'OptionAdvance',
      'OptionExAmount',
      'OptionFinAmount',
      'DistroFee',
      'LegalAmount',
      'LP2Min',
      'LP2Max',
      'LP3Min',
      'LP3Max',
      'RoyaltyRate',
    ]),
  },
  {
    id: 'catalog',
    title: 'Catalog',
    fields: new Set(['CatalogTrackNames']),
  },
]

export const TOGGLE_SCOPE = {
  include_catalog: 'distribution',
  redelivery_advance: 'distribution',
  include_legal: 'distribution',
  include_marketing: 'distribution',
  proposal_term_length: 'proposal',
  discretionary_marketing: 'longform',
  with_options: 'longform',
  publishing_addendum: 'longform',
}

export const TOGGLE_SCOPE_LABELS = {
  distribution: 'Distribution',
  longform: 'Longform',
  proposal: 'Proposal',
}

const FORM_SUFFIX = {
  proposal: 'Distribution_Proposal',
  longform: 'Distribution_Longform',
  frontline_proposal: 'Frontline_Proposal',
}

const FORM_LABEL = {
  proposal: 'Proposal',
  longform: 'Longform',
  frontline_proposal: 'Frontline Proposal',
}

export function safeDealFilename(name) {
  const trimmed = String(name || 'Artist').trim() || 'Artist'
  const cleaned = trimmed.replace(/\s+/g, '_').replace(/[^\w.-]+/g, '')
  return cleaned || 'Artist'
}

export function exportPreviewFilename(artistName, forms) {
  const kinds = ['proposal', 'longform', 'frontline_proposal'].filter((k) => forms[k])
  if (!kinds.length) return null

  const date = new Date().toISOString().slice(0, 10)
  const artist = safeDealFilename(artistName)

  if (kinds.length === 1) {
    const kind = kinds[0]
    return `${artist}_${FORM_SUFFIX[kind]}_${date}.docx`
  }

  return kinds.map((k) => `${artist}_${FORM_SUFFIX[k]}_${date}.docx`).join(' + ')
}

export function exportFormSummary(forms, toggles, values) {
  const formLabels = ['proposal', 'longform', 'frontline_proposal']
    .filter((k) => forms[k])
    .map((k) => FORM_LABEL[k])
  const toggleOn = Object.entries(toggles || {}).filter(([, v]) => v === true).length
  if (!formLabels.length) return 'No forms selected'
  const togglePart = toggleOn ? ` · ${toggleOn} option${toggleOn === 1 ? '' : 's'} on` : ''
  const detailParts = []
  if (values) {
    const marketing = describeMarketingBudget(values, toggles, forms)
    if (marketing) detailParts.push(marketing)
    const legal = describeLegalAdvance(values, toggles, forms)
    if (legal) detailParts.push(legal)
  }
  const detailPart = detailParts.length ? ` · ${detailParts.join(' · ')}` : ''
  return `${formLabels.join(' + ')}${togglePart}${detailPart}`
}

export function describeMarketingBudget(values, toggles, forms) {
  const usesMarketing =
    (forms?.proposal || forms?.longform || forms?.frontline_proposal) &&
    toggles?.include_marketing !== false
  if (!usesMarketing) {
    return toggles?.include_marketing === false ? 'Marketing omitted' : null
  }
  const raw = String(values?.MarketingAmount ?? '').trim()
  if (!raw || MARKETING_NO_CAP_TOKENS.has(raw.toLowerCase())) {
    return 'Marketing: mutually approved'
  }
  return `Marketing: ${raw}`
}

export function describeLegalAdvance(values, toggles, forms) {
  const usesLegal = (forms?.proposal || forms?.longform) && toggles?.include_legal !== false
  if (!usesLegal) {
    return toggles?.include_legal === false ? 'Legal omitted' : null
  }
  const raw = String(values?.LegalAmount ?? '').trim()
  if (!raw || LEGAL_OMIT_TOKENS.has(raw.toLowerCase())) {
    return 'Legal omitted'
  }
  return `Legal: ${raw}`
}

export function fieldInputHint(fieldName, { forms, toggles } = {}) {
  if (fieldName === 'MarketingAmount' && toggles?.include_marketing !== false) {
    const frontlineOnly =
      forms?.frontline_proposal && !forms?.proposal && !forms?.longform
    if (frontlineOnly) {
      return 'Frontline proposal §7. Blank or MA = mutually approved (no cap).'
    }
    return 'Blank or MA = mutually approved (no cap).'
  }
  if (fieldName === 'LegalAmount' && toggles?.include_legal !== false) {
    return 'Blank or na = omit legal advance clause.'
  }
  return null
}

export function groupVisibleFields(visibleFields) {
  const assigned = new Set()
  const groups = FIELD_GROUPS.map((group) => {
    const fields = visibleFields.filter((f) => group.fields.has(f.name))
    fields.forEach((f) => assigned.add(f.name))
    return { ...group, fields }
  }).filter((g) => g.fields.length > 0)

  const other = visibleFields.filter((f) => !assigned.has(f.name))
  if (other.length) {
    groups.push({ id: 'other', title: 'Other', fields: other })
  }
  return groups
}

export function placeholderForField(field) {
  if (field?.name === 'MarketingAmount') {
    return 'e.g. $75,000 or MA'
  }
  if (field?.name === 'LegalAmount') {
    return 'e.g. $1,500 or blank to omit'
  }
  switch (field?.value_kind) {
    case 'currency':
      return 'e.g. $75,000'
    case 'percent':
      return 'e.g. 30%'
    case 'currency_or_percent':
      return 'e.g. $25,000 or 50%'
    default:
      return ''
  }
}

export function loadDealsDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

export function saveDealsDraft(payload) {
  try {
    sessionStorage.setItem(
      DRAFT_STORAGE_KEY,
      JSON.stringify({ ...payload, savedAt: Date.now() }),
    )
  } catch {
    // sessionStorage full or unavailable
  }
}

export function clearDealsDraft() {
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function schemaCacheVersionMatches() {
  try {
    if (typeof sessionStorage === 'undefined') return true
    return (
      sessionStorage.getItem(SCHEMA_CACHE_VERSION_KEY) ===
      String(DEAL_FORMS_SCHEMA_CACHE_VERSION)
    )
  } catch {
    return true
  }
}

export function markSchemaCacheVersion() {
  try {
    sessionStorage.setItem(
      SCHEMA_CACHE_VERSION_KEY,
      String(DEAL_FORMS_SCHEMA_CACHE_VERSION),
    )
  } catch {
    // ignore
  }
}

export function clearSchemaCacheVersion() {
  try {
    sessionStorage.removeItem(SCHEMA_CACHE_VERSION_KEY)
  } catch {
    // ignore
  }
}

export function formatDraftTime(savedAt) {
  if (!savedAt) return 'earlier'
  try {
    return new Date(savedAt).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return 'earlier'
  }
}
